import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Select, Tooltip } from 'antd';
import EllipsisCell from '../../../components/common/EllipsisCell';

const COLORS = {
  danger: '#FF4D4F',
  warning: '#FAAD14',
  blue: '#1890ff',
};

/**
 * Background color for duration badge by rank
 * Rank 1 = lightest warm red, Rank 10 = darkest warm red
 * Uses HSL interpolation for a smooth continuous gradient.
 */
const getDurationBadgeStyle = (index) => {
  // Clamp index to 0..9 (rank 1..10)
  const normalizedIndex = Math.min(Math.max(index, 0), 9);
  const t = normalizedIndex / 9;

  // Interpolate HSL in the warm red/orange range.
  // Rank 1: light coral (h=10, s=75%, l=88%)
  // Rank 10: deep red (h=5, s=90%, l=38%)
  const h = 10 - t * 5;
  const s = 75 + t * 15;
  const l = 88 - t * 50;

  const topL = Math.min(l + 6, 96);
  const midL = l;
  const bottomL = Math.max(l - 4, 22);

  const background = `linear-gradient(180deg, hsl(${h}, ${s}%, ${topL}%) 0%, hsl(${h}, ${s}%, ${midL}%) 50%, hsl(${h}, ${s}%, ${bottomL}%) 100%)`;
  const color = l > 55 ? '#7F1D1D' : '#FFFFFF';

  return {
    border: 'none',
    WebkitFontSmoothing: 'antialiased',
    background,
    color,
    boxShadow: `0 4px 6px -1px hsla(${h}, ${s}%, ${midL}%, 0.25), 0 2px 4px -2px hsla(${h}, ${s}%, ${midL}%, 0.15), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.05)`,
  };
};

/**
 * Rank medal for top 3
 */
const getRankDisplay = (index) => {
  if (index === 0) return <span style={{ fontSize: 18 }}>🥇</span>;
  if (index === 1) return <span style={{ fontSize: 18 }}>🥈</span>;
  if (index === 2) return <span style={{ fontSize: 18 }}>🥉</span>;
  return <span style={{ fontSize: 14, color: '#999', fontWeight: 'bold' }}>{index + 1}</span>;
};

/**
 * Module 3: Rankings & Actionables
 * Store Duration Table (with medals + duration badge) + Shortage Items Table
 */
const STORE_TYPE_OPTIONS = [
  { value: 'Go Fresh', label: 'Go Fresh' },
  { value: 'Super', label: 'Super' },
  { value: 'Hyper', label: 'Hyper' },
];

const RankingsPanel = ({ data, storeType, onStoreTypeChange }) => {
  const { t } = useTranslation();

  const storeDurationRanking = data?.storeDurationRanking || [];
  const shortageItemRanking = data?.shortageItemRanking || [];

  if (!data) return null;

  // Store Duration Table columns - equal width, no extra whitespace
  const storeColumns = [
    {
      title: '#',
      key: 'rank',
      width: '6%',
      align: 'center',
      render: (_, record, index) => getRankDisplay(index),
    },
    {
      title: t('dashboard.nationwide.storeName'),
      dataIndex: 'storeName',
      key: 'storeName',
      width: '30%',
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('dashboard.nationwide.storeCode'),
      dataIndex: 'storeCode',
      key: 'storeCode',
      width: '16%',
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('dashboard.nationwide.type'),
      dataIndex: 'storeType',
      key: 'storeType',
      width: '16%',
      align: 'center',
      render: (type) => {
        const typeConfig = {
          'Go Fresh': { color: '#52C41A', bg: '#F6FFED' },
          'Super': { color: '#1890FF', bg: '#E6F7FF' },
          'Hyper': { color: '#722ED1', bg: '#F9F0FF' },
        };
        const config = typeConfig[type] || { color: '#666', bg: '#F5F5F5' };
        return (
          <span style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: 12,
            fontSize: 12,
            fontWeight: 600,
            color: config.color,
            background: config.bg,
            border: `1px solid ${config.color}30`,
          }}>
            {type}
          </span>
        );
      },
    },
    {
      title: t('dashboard.nationwide.totalDurationPerOrder'),
      key: 'avgDuration',
      align: 'center',
      render: (_, record, index) => {
        const badgeStyle = getDurationBadgeStyle(index);
        return (
          <span
            style={{
              display: 'inline-block',
              padding: '6px 18px',
              borderRadius: 20,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", sans-serif',
              letterSpacing: '0.2px',
              WebkitFontSmoothing: 'antialiased',
              backdropFilter: 'blur(8px)',
              transition: 'all 0.2s ease',
              ...badgeStyle,
            }}
          >
            {Number(record.avgDuration).toFixed(2)} min
          </span>
        );
      },
    },
  ];

  // 11. Shortage Items Ranking - 表格展示：SKU / 名称 / 缺货订单数 / 受影响门店数
  const shortageColumns = [
    {
      title: t('dashboard.nationwide.sku'),
      dataIndex: 'sku',
      key: 'sku',
      width: '20%',
      render: (text) => <EllipsisCell>{text || '-'}</EllipsisCell>,
    },
    {
      title: t('dashboard.nationwide.itemName'),
      dataIndex: 'itemName',
      key: 'itemName',
      render: (text) => <EllipsisCell>{text || '-'}</EllipsisCell>,
    },
    {
      title: t('dashboard.nationwide.shortageOrder'),
      dataIndex: 'shortageCount',
      key: 'shortageCount',
      width: '24%',
      align: 'right',
    },
    {
      title: t('dashboard.nationwide.effectStore'),
      dataIndex: 'affectedStoreCodes',
      key: 'affectedStoreCodes',
      width: '18%',
      align: 'right',
      // 展示受影响门店数量，完整编码列表 hover 浮显
      render: (codes) => {
        const list = codes || [];
        return list.length ? (
          <Tooltip title={list.join(', ')}>
            <span>{list.length}</span>
          </Tooltip>
        ) : (
          0
        );
      },
    },
  ];

  return (
    <div style={{ display: 'flex', gap: 16, minWidth: 0 }}>
      {/* 10. Store Duration Table with medals and duration badges */}
      <Card
        title={t('dashboard.nationwide.storeDurationTitle')}
        extra={
          <Select
            placeholder={t('dashboard.nationwide.selectStoreType')}
            value={storeType}
            onChange={(value) => onStoreTypeChange(value)}
            options={STORE_TYPE_OPTIONS}
            size="small"
            style={{ width: 160 }}
          />
        }
        style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
        styles={{ body: { padding: '0 0 8px 0' } }}
      >
        <Table
          columns={storeColumns}
          dataSource={storeDurationRanking}
          rowKey="storeCode"
          pagination={false}
          size="small"
          scroll={{ y: 340 }}
          tableLayout="fixed"
        />
      </Card>

      {/* 11. Shortage Items Ranking */}
      <Card
        title={t('dashboard.nationwide.shortageItemsRanking')}
        style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
        styles={{ body: { padding: '0 0 8px 0' } }}
      >
        <Table
          columns={shortageColumns}
          dataSource={shortageItemRanking}
          rowKey="sku"
          pagination={false}
          size="small"
          scroll={{ y: 340 }}
          tableLayout="fixed"
        />
      </Card>
    </div>
  );
};

export default RankingsPanel;
